import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Loader from "@/components/loader";
import { getSafeRedirectPath, useAuth } from "@/lib/auth-hooks";

interface AuthGuardProps {
  children: ReactNode;
  redirectTo?: string;
  fallback?: ReactNode;
}

export function AuthGuard({
  children,
  redirectTo = "/sign-in",
  fallback,
}: AuthGuardProps) {
  const { isAuthenticated, isPending } = useAuth();

  if (isPending) {
    return fallback || <Loader className="absolute inset-0 h-full" />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

interface PublicGuardProps {
  children: ReactNode;
  redirectTo?: string;
  fallback?: ReactNode;
}

export function PublicGuard({
  children,
  redirectTo = "/dashboard",
  fallback,
}: PublicGuardProps) {
  const { isAuthenticated, isPending } = useAuth();
  const location = useLocation();

  if (isPending) {
    return fallback || <Loader className="absolute inset-0 h-full" />;
  }

  if (isAuthenticated) {
    return (
      <Navigate to={getSafeRedirectPath(location.search, redirectTo)} replace />
    );
  }

  return <>{children}</>;
}
